/**
 * PaymentPage Component
 *
 * Payment page for confirmed bookings. Supports Stripe card
 * and Whish Money payment methods.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { PublicLayout, Container } from '../../components/layout';
import { useAuth } from '../../contexts';
import { db, functions } from '../../services/firebase';
import { useBookingDetails } from './hooks';
import { PaymentSelection, type PaymentMethod } from './components/PaymentSelection';
import { WhishInstructionsModal } from './components/WhishInstructionsModal';

/** Reads the admin kill switch for the Whish Pay hosted-payment flow. */
function useWhishApiEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'platformConfig', 'current'))
      .then((snap) => {
        if (!cancelled) {
          setEnabled(snap.data()?.payments?.whishPayEnabled === true);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return enabled;
}

// Format date safely
const formatDate = (date: Date | unknown): string => {
  if (!date) return 'N/A';
  if (date && typeof date === 'object' && 'toDate' in date && typeof (date as { toDate: () => Date }).toDate === 'function') {
    date = (date as { toDate: () => Date }).toDate();
  }
  const d = date instanceof Date ? date : new Date(date as string | number);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const formatCurrency = (amount: number): string => {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

export const PaymentPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { booking, isLoading } = useBookingDetails(bookingId);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('stripe');
  const [processing, setProcessing] = useState(false);
  const [manualSent, setManualSent] = useState(false);
  const [showWhishModal, setShowWhishModal] = useState(false);
  const whishApiEnabled = useWhishApiEnabled();
  const [whishVerifying, setWhishVerifying] = useState(false);

  const urlStatus = searchParams.get('status');
  const urlProvider = searchParams.get('provider');

  // Whish redirect-return fallback: the success callback can be lost, so
  // verify the collect status directly when landing back unpaid.
  useEffect(() => {
    if (
      urlProvider !== 'whish' ||
      urlStatus !== 'success' ||
      !bookingId ||
      !booking ||
      booking.paymentStatus === 'paid'
    ) {
      return;
    }
    setWhishVerifying(true);
    const verify = httpsCallable<{ bookingId: string }, { paid: boolean }>(
      functions,
      'verifyWhishPayment'
    );
    verify({ bookingId })
      .then((result) => {
        if (result.data.paid) {
          window.location.replace(
            `/booking/${bookingId}/pay?status=success&provider=whish&verified=1`
          );
        } else {
          toast.error('Payment not confirmed yet. If you completed it, check back in a minute.');
        }
      })
      .catch(() => toast.error('Could not verify your Whish payment.'))
      .finally(() => setWhishVerifying(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProvider, urlStatus, bookingId, booking?.paymentStatus]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Handle Stripe card payment
  const handleStripePayment = useCallback(async () => {
    if (!bookingId) return;
    setProcessing(true);

    try {
      const createSession = httpsCallable<
        { bookingId: string },
        { sessionUrl: string }
      >(functions, 'createStripeSession');

      const result = await createSession({ bookingId });
      const { sessionUrl } = result.data;

      if (sessionUrl) {
        window.location.href = sessionUrl;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Payment failed';
      toast.error(message);
    } finally {
      setProcessing(false);
    }
  }, [bookingId]);

  // Handle manual payment "I've Sent" button (Whish Money)
  const handleManualSent = useCallback(async () => {
    if (!bookingId) return;
    setProcessing(true);

    try {
      const flagManual = httpsCallable<
        { bookingId: string; method: string },
        { success: boolean }
      >(functions, 'flagManualPaymentSent');

      await flagManual({ bookingId, method: paymentMethod });
      setManualSent(true);
      setShowWhishModal(false);
      toast.success('Payment flagged. Our team will verify it shortly.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to flag payment';
      toast.error(message);
    } finally {
      setProcessing(false);
    }
  }, [bookingId, paymentMethod]);

  // Handle Whish hosted-payment redirect (API mode)
  const handleWhishPayment = useCallback(async () => {
    if (!bookingId) return;
    setProcessing(true);

    try {
      const createSession = httpsCallable<
        { bookingId: string },
        { collectUrl: string }
      >(functions, 'createWhishSession');

      const result = await createSession({ bookingId });
      if (result.data.collectUrl) {
        window.location.href = result.data.collectUrl;
      } else {
        toast.error('Failed to start Whish payment');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Payment failed';
      toast.error(
        message.includes('WHISH_API_DISABLED')
          ? 'Whish online payment is unavailable right now. Use the manual transfer instructions instead.'
          : message
      );
    } finally {
      setProcessing(false);
    }
  }, [bookingId]);

  // Handle "Pay Now" button click
  const handlePayNow = () => {
    if (paymentMethod === 'stripe') {
      handleStripePayment();
    } else if (paymentMethod === 'whish_money' && whishApiEnabled) {
      handleWhishPayment();
    }
  };

  if (authLoading || isLoading) {
    return (
      <PublicLayout>
        <Container className="py-12">
          <PaymentSkeleton />
        </Container>
      </PublicLayout>
    );
  }

  if (!booking) {
    return (
      <PublicLayout>
        <Container className="py-12 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Booking not found</h1>
          <p className="text-gray-600 mb-4">We couldn't find this booking.</p>
          <Link to="/" className="text-primary-600 hover:underline">Return to home</Link>
        </Container>
      </PublicLayout>
    );
  }

  // Guard: must be the guest
  if (user && booking.guestId !== user.uid) {
    return (
      <PublicLayout>
        <Container className="py-12 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access denied</h1>
          <p className="text-gray-600 mb-4">Only the booking guest can access this payment page.</p>
          <Link to="/" className="text-primary-600 hover:underline">Return to home</Link>
        </Container>
      </PublicLayout>
    );
  }

  // Guard: booking must be confirmed
  if (booking.status !== 'confirmed') {
    return (
      <PublicLayout>
        <Container className="py-12 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment not available</h1>
          <p className="text-gray-600 mb-4">
            This booking must be confirmed by the host before payment can be made.
          </p>
          <Link to={`/booking/confirmation/${bookingId}`} className="text-primary-600 hover:underline">
            View booking details
          </Link>
        </Container>
      </PublicLayout>
    );
  }

  // Already paid
  if (booking.paymentStatus === 'paid') {
    return (
      <PublicLayout>
        <Container className="py-12">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckIcon className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Payment Complete!</h1>
            <p className="text-lg text-gray-600 mb-8">
              Your payment has been received. You're all set for your stay!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/dashboard/bookings"
                className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors"
              >
                View My Bookings
              </Link>
              <Link
                to="/"
                className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </Container>
      </PublicLayout>
    );
  }

  // Whish return: still confirming with the Whish API
  if (urlStatus === 'success' && urlProvider === 'whish') {
    return (
      <PublicLayout>
        <Container className="py-12">
          <div className="max-w-lg mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Confirming your payment...
            </h1>
            <p className="text-gray-600">
              {whishVerifying
                ? 'We are verifying your Whish payment. This takes a few seconds.'
                : 'If you completed the payment, it will be confirmed shortly.'}
            </p>
          </div>
        </Container>
      </PublicLayout>
    );
  }

  // Stripe return: success
  if (urlStatus === 'success') {
    return (
      <PublicLayout>
        <Container className="py-12">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckIcon className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Payment Successful!</h1>
            <p className="text-lg text-gray-600 mb-8">
              Your payment is being processed. You'll receive a confirmation notification shortly.
            </p>
            <Link
              to="/dashboard/bookings"
              className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors"
            >
              View My Bookings
            </Link>
          </div>
        </Container>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <Container className="py-8 md:py-12">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Complete Payment</h1>
          <p className="text-gray-600 mb-8">Choose your preferred payment method to secure your booking.</p>

          {urlStatus === 'cancelled' && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
              <AlertIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Payment cancelled</p>
                <p className="text-sm text-amber-700">Your payment was not completed. You can try again below.</p>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-5 gap-8">
            {/* Left: Payment methods */}
            <div className="md:col-span-3">
              <PaymentSelection
                value={paymentMethod}
                onChange={setPaymentMethod}
                whishApiEnabled={whishApiEnabled}
              />

              {/* Action area */}
              <div className="mt-6">
                {(paymentMethod === 'stripe' ||
                  (paymentMethod === 'whish_money' && whishApiEnabled)) && (
                  <button
                    onClick={handlePayNow}
                    disabled={processing}
                    className="w-full py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing
                      ? 'Redirecting to checkout...'
                      : `Pay ${formatCurrency(booking.pricing.totalAmount)}`}
                  </button>
                )}

                {paymentMethod === 'whish_money' && !whishApiEnabled && (
                  <>
                    {!manualSent ? (
                      <button
                        onClick={() => setShowWhishModal(true)}
                        disabled={processing}
                        className="w-full py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Pay with Whish
                      </button>
                    ) : (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center">
                        <CheckIcon className="w-6 h-6 text-green-600 mx-auto mb-2" />
                        <p className="font-medium text-green-800">Thank you!</p>
                        <p className="text-sm text-green-700 mt-1">
                          Your payment is awaiting verification by our team. You'll receive a notification once it's confirmed.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Right: Booking summary */}
            <div className="md:col-span-2">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 sticky top-24">
                <h3 className="font-semibold text-gray-900 mb-4">Booking Summary</h3>

                {booking.listing && (
                  <div className="flex gap-3 pb-4 border-b border-gray-200 mb-4">
                    <img
                      src={booking.listing.photo}
                      alt={booking.listing.title}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div>
                      <h4 className="font-medium text-gray-900 text-sm">{booking.listing.title}</h4>
                      <p className="text-xs text-gray-500 mt-1">{booking.listing.location}</p>
                    </div>
                  </div>
                )}

                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Check-in</dt>
                    <dd className="font-medium text-gray-900">{formatDate(booking.checkIn)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Check-out</dt>
                    <dd className="font-medium text-gray-900">{formatDate(booking.checkOut)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Guests</dt>
                    <dd className="font-medium text-gray-900">
                      {booking.guests.adults} adult{booking.guests.adults !== 1 ? 's' : ''}
                    </dd>
                  </div>
                </dl>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">
                        {formatCurrency(booking.pricing.nightlyRate)} x {booking.nights} night{booking.nights !== 1 ? 's' : ''}
                      </dt>
                      <dd className="text-gray-900">{formatCurrency(booking.pricing.nightlyTotal)}</dd>
                    </div>
                    {booking.pricing.cleaningFee > 0 && (
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Cleaning fee</dt>
                        <dd className="text-gray-900">{formatCurrency(booking.pricing.cleaningFee)}</dd>
                      </div>
                    )}
                    {booking.pricing.securityDeposit > 0 && (
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Security deposit</dt>
                        <dd className="text-gray-900">{formatCurrency(booking.pricing.securityDeposit)}</dd>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <dt className="font-semibold text-gray-900">Total</dt>
                      <dd className="font-semibold text-gray-900">
                        {formatCurrency(booking.pricing.totalAmount)}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="mt-4 text-xs text-gray-500">
                  Confirmation code: <span className="font-mono">{bookingId}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>

      <WhishInstructionsModal
        isOpen={showWhishModal}
        onClose={() => setShowWhishModal(false)}
        bookingCode={bookingId || ''}
        amount={formatCurrency(booking.pricing.totalAmount)}
        isSubmitting={processing}
        onConfirmSent={handleManualSent}
      />
    </PublicLayout>
  );
};

// Loading skeleton
const PaymentSkeleton: React.FC = () => (
  <div className="max-w-5xl mx-auto animate-pulse">
    <div className="h-10 bg-gray-200 rounded w-64 mb-3" />
    <div className="h-5 bg-gray-200 rounded w-96 mb-8" />
    <div className="grid md:grid-cols-5 gap-8">
      <div className="md:col-span-3 space-y-3">
        <div className="h-20 bg-gray-200 rounded-xl" />
        <div className="h-20 bg-gray-200 rounded-xl" />
      </div>
      <div className="md:col-span-2">
        <div className="h-72 bg-gray-200 rounded-2xl" />
      </div>
    </div>
  </div>
);

// Icons
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const AlertIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

export default PaymentPage;
